// src/components/interactive/PHIChecklistComponent.js - Printable checklist for PHI email handling
import React, { useRef } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';
import aransciaLogo from '../../assets/images/aranscia-logo.svg';

const ChecklistContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-lg);
`;

const ChecklistHeader = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderTitle = styled.h4`
  margin: 0;
  font-size: 1.2rem;
`;

const ChecklistTitle = styled.div`
  h2 {
    margin: 0;
    color: var(--primary-color);
  }
  
  p {
    margin: 0;
    font-weight: 500;
    color: var(--text-color);
  }
  
  @media print {
    h2 {
      color: #000;
      font-size: 18pt;
    }
    
    p {
      color: #000;
    }
  }
`;

const PrintableContent = styled.div`
  padding: var(--spacing-lg);
  background-color: var(--background-color);
  color: var(--text-color);

  @media print {
    padding: 0;
    background-color: white;
    color: black;
    font-size: 12pt;
  }
`;

const ChecklistSection = styled.div`
  margin-bottom: var(--spacing-xl);
  page-break-inside: avoid;
`;

const SectionTitle = styled.h3`
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  color: var(--text-color);
  
  @media print {
    color: black;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid var(--primary-color);
  border-radius: 4px;
  flex-shrink: 0;
  margin-top: 2px;

  @media print {
    border: 2px solid #000;
  }
`;

const ItemContent = styled.div`
  flex: 1;
`;

const ItemTitle = styled.div`
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
  color: var(--text-color);
  
  @media print {
    color: black;
  }
`;

const ItemDescription = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--text-color-light);
  
  @media print {
    color: black;
  }
`;

const LogoSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  padding-bottom: var(--spacing-lg);
  
  @media print {
    border-bottom: 1px solid #000;
  }
`;

const LogoContainer = styled.div`
  background-color: #17274f; /* Aranscia dark blue */
  border-radius: var(--border-radius);
  padding: var(--spacing-sm) var(--spacing-md);
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media print {
    background-color: #17274f !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  
  @media print {
    height: 35px;
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  padding-top: var(--spacing-lg);

  @media print {
    display: none;
  }
`;

const FooterNote = styled.div`
  font-style: italic;
  margin-top: var(--spacing-lg);
  font-size: 0.9rem;
  color: var(--text-color-light);
  text-align: center;
  border-top: 1px solid var(--border-color);
  padding-top: var(--spacing-md);
  
  @media print {
    font-size: 10pt;
    color: black;
    border-top: 1px solid #ccc;
  }
`;

const DateSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: var(--spacing-md);
  color: var(--text-color);
  
  @media print {
    color: black;
  }
`;

const PHIChecklistComponent = () => {
  const checklistRef = useRef(null);
  
  const handlePrint = () => {
    window.print();
  };
  
  const currentDate = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  return (
    <ChecklistContainer>
      <ChecklistHeader>
        <HeaderTitle>Protected Health Information (PHI) Email Checklist</HeaderTitle>
        <div>Printable Reference</div>
      </ChecklistHeader>
      
      <PrintableContent ref={checklistRef}>
        <LogoSection>
          <LogoContainer>
            <LogoImage src={aransciaLogo} alt="Aranscia Logo" />
          </LogoContainer>
          <ChecklistTitle>
            <h2>PHI Email Handling Checklist</h2>
            <p>Official Guidelines for All Staff</p>
          </ChecklistTitle>
        </LogoSection>
        
        <DateSection>
          <div><strong>Last Updated:</strong> {currentDate}</div>
        </DateSection>
        
        <ChecklistSection>
          <SectionTitle>Before Sending PHI via Email</SectionTitle>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Verify necessity</ItemTitle>
              <ItemDescription>
                Confirm that the PHI must be sent via email and cannot be communicated through a more secure method
                (e.g., patient portal, secure messaging app).
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Confirm recipient need-to-know</ItemTitle>
              <ItemDescription>
                Verify that each recipient has a legitimate need to receive the PHI as part of their job duties.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Limit PHI included</ItemTitle>
              <ItemDescription>
                Include only the minimum necessary PHI required to fulfill the purpose of the communication.
                Avoid sending full medical records or unnecessary patient details.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Verify recipient email addresses</ItemTitle>
              <ItemDescription>
                Double-check all email addresses in the To, CC, and BCC fields to ensure they are correct
                and that no unintended recipients are included.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
        </ChecklistSection>
        
        <ChecklistSection>
          <SectionTitle>Encryption Requirements</SectionTitle>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Manual encryption method (Outlook)</ItemTitle>
              <ItemDescription>
                Go to Options &gt; Permission and select "Encrypt" to manually encrypt the email. For even stronger
                protection, select "Do Not Forward" to prevent recipients from forwarding, copying, or printing the email.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Subject line keyword method</ItemTitle>
              <ItemDescription>
                Include "PHI" in the subject line to trigger automatic encryption when sending emails with
                protected health information. For other confidential information, include "Encrypt" in the subject line.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
        </ChecklistSection>
        
        <ChecklistSection>
          <SectionTitle>Sending to External Recipients</SectionTitle>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Verify external sharing permissions</ItemTitle>
              <ItemDescription>
                Confirm that sharing PHI with the external recipient is permitted under HIPAA and your
                organization's policies.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Inform recipients about secure email process</ItemTitle>
              <ItemDescription>
                Let external recipients know they will receive a secure link and may need to use a
                one-time passcode that expires in 15 minutes.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Consider Business Associate Agreement</ItemTitle>
              <ItemDescription>
                If regularly sharing PHI with an external entity, ensure a Business Associate Agreement
                (BAA) is in place if required.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
        </ChecklistSection>
        
        <ChecklistSection>
          <SectionTitle>Incident Response</SectionTitle>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Immediate notification</ItemTitle>
              <ItemDescription>
                If you accidentally send PHI in an unencrypted email or to the wrong recipient,
                immediately notify Security at: itsecurity@aranscia.com.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Document the incident</ItemTitle>
              <ItemDescription>
                Record what happened, when it happened, who was involved, and what PHI was exposed.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
          
          <ChecklistItem>
            <Checkbox />
            <ItemContent>
              <ItemTitle>Cooperate with investigation</ItemTitle>
              <ItemDescription>
                Work with the Security and Privacy teams to address the incident and prevent future occurrences.
              </ItemDescription>
            </ItemContent>
          </ChecklistItem>
        </ChecklistSection>
        
        <FooterNote>
          This checklist is intended as a quick reference guide. Always refer to the complete PHI Email Handling
          Policy for comprehensive guidelines. For questions, contact the Security team at itsecurity@aranscia.com.
        </FooterNote>
        
        <Controls>
          <Button onClick={handlePrint}>
            Print Checklist
          </Button>
        </Controls>
      </PrintableContent>
    </ChecklistContainer>
  );
};

export default PHIChecklistComponent; 